import { forwardRef, useRef, type ReactNode } from "react";
import { ClockIcon } from "@heroicons/react/24/outline";

import { formatCurrency } from "helpers/currency";

import type {
  ServiceQuestion as ServiceQuestionType,
  ServiceAnswer,
  PackageInclusions,
  DcOrderingTemplate,
} from "./types";
import {
  selfServeCreditAmount,
  defaultDcOrdering,
  QUESTION_ID,
  DC_ORDERING,
} from "./helpers";
import DeathCertStepper from "./DeathCertStepper";
import { AFTER_ORDERS_TIMELINE_FALLBACK } from "./dcOrderingTemplates";

type Props = {
  question: ServiceQuestionType;
  /** The quantity answer (`q-death-certs`). */
  answer: ServiceAnswer | undefined;
  /** The who-orders answer (`q-dc-ordering`). */
  orderingAnswer: ServiceAnswer | undefined;
  onAnswer: (answer: ServiceAnswer) => void;
  packageInclusions?: PackageInclusions | null;
  /** County/state ordering guidance. Printed as written; never interpolated. */
  template?: DcOrderingTemplate | null;
  /**
   * How long each path takes. Separate from the template because timing varies
   * county by county inside one template, while the prose doesn't. Either may
   * be absent, in which case that option simply shows no timing line rather
   * than a guess.
   */
  timelineFamilyOrders?: string | null;
  timelineAfterOrders?: string | null;
  /**
   * The loved one's first name. Passed in rather than read from
   * ArrangementContext so this component stays pure — it renders from props
   * alone, which is what lets the dev preview route mount it with no provider.
   */
  lovedOneName: string;
  /** Matches the other questions: h1 on the wizard path, h2 in the read-only view. */
  headingLevel?: 1 | 2;
  /**
   * Which option to show, when something outside this component decides it —
   * the case's own preference outranks both the draft answer and the package
   * default. Falls back to the answer, then the default, when absent.
   */
  selectedOrdering?: string | null;
  /**
   * Renders the whole question as a statement rather than a choice. Set once
   * the certificates have been ordered, when neither half is still the
   * family's to change.
   */
  readOnly?: boolean;
  /** How many copies the case records as ordered, once an order exists. */
  orderedQuantity?: number;
};

export default function DeathCertificatesQuestion({
  question,
  answer,
  orderingAnswer,
  onAnswer,
  packageInclusions,
  template,
  timelineFamilyOrders,
  timelineAfterOrders,
  lovedOneName,
  headingLevel = 1,
  selectedOrdering: selectedOrderingOverride,
  readOnly = false,
  orderedQuantity,
}: Props) {
  const Heading = headingLevel === 1 ? "h1" : "h2";

  const config = question.stepperConfig;
  const firstCopyPrice = config?.firstCopyPrice ?? 0;
  const additionalCopyPrice = config?.additionalCopyPrice ?? 0;

  const included =
    packageInclusions?.includedDeathCerts ?? config?.includedQuantity ?? 0;
  const credit = selfServeCreditAmount(
    included,
    firstCopyPrice,
    additionalCopyPrice,
  );
  const hasCredit = credit > 0;
  const copies = included === 1 ? "copy" : "copies";

  const selectedOrdering =
    selectedOrderingOverride ??
    orderingAnswer?.selectedOptionId ??
    defaultDcOrdering(included);
  const isFamily = selectedOrdering === DC_ORDERING.FAMILY;
  const isAfter = selectedOrdering === DC_ORDERING.AFTER;

  const chooseOrdering = (value: string) =>
    onAnswer({
      questionId: QUESTION_ID.DC_ORDERING,
      selectedOptionId: value,
    });

  const familyRef = useRef<HTMLDivElement>(null);
  const afterRef = useRef<HTMLDivElement>(null);

  // Arrow keys move the selection and the focus together, which is the radio
  // group pattern. With two options every direction lands on the other one, so
  // the group also wraps as the pattern expects.
  const moveTo = (value: string) => {
    chooseOrdering(value);
    (value === DC_ORDERING.AFTER ? afterRef : familyRef).current?.focus();
  };

  return (
    <div>
      <div className="text-center">
        <Heading className="mb-3 font-serif text-3xl text-soil">
          Death certificates for <span className="italic">{lovedOneName}</span>
        </Heading>
        {question.note && (
          <p className="mx-auto mb-4 max-w-xl text-lg text-soil">
            {question.note}
          </p>
        )}
      </div>

      {/*
        Shown whichever option is selected, so it may only state what is true
        of both. Anything about one path belongs inside that option.

        Names no issuing office. Who issues the permit varies by market —
        county clerk, state office, local health department — so passive voice
        keeps this true everywhere. Naming an office is the template's job.
      */}
      <div className="my-7 flex items-start gap-2.5 text-base leading-relaxed text-soil">
        <ClockIcon className="mt-1 h-4 w-4 shrink-0 text-olive" />
        <span>
          Certificates can be ordered once the permit is issued and our funeral
          director signs it,{" "}
          <span className="font-bold text-evergreen">
            typically 7 to 10 days after we pick your loved one up
          </span>
          .
        </span>
      </div>

      {/* Self-serve leads: it is the outcome the project is trying to make
          easy to choose, and on packages including no certificates it is the
          default. Packages that include certificates preselect nothing. */}
      <div
        className="space-y-3"
        role="radiogroup"
        aria-label="Who orders the death certificates"
      >
        <OrderingOption
          ref={familyRef}
          label="Order them yourself"
          tagline="Best if you need them fast"
          selected={isFamily}
          // With nothing chosen yet, Tab lands on the first option.
          tabbable={isFamily || !selectedOrdering}
          readOnly={readOnly}
          onSelect={() => chooseOrdering(DC_ORDERING.FAMILY)}
          onMoveSelection={() => moveTo(DC_ORDERING.AFTER)}
          price={
            hasCredit ? (
              <span className="font-bold text-evergreen">
                {formatCurrency(credit)} back
              </span>
            ) : null
          }
          timing={timelineFamilyOrders}
        >
          {hasCredit && (
            <p>
              We credit{" "}
              <span className="font-bold text-evergreen">
                {formatCurrency(credit)}
              </span>{" "}
              back to your order, the value of the {included} {copies} in your
              package.
            </p>
          )}
          <p>
            We&rsquo;ll email you a guide on how to order the day the permit
            clears. You pay for the copies directly and can order as many as you
            need.
          </p>
          {/* "Many", not all: a walk-in counter exists in 25 of 119 counties,
              and in the 94 VitalChek counties a family's own order is
              mail-only and takes as long as ours. */}
          <p>
            Many counties give you tracking or someone to call, which they
            won&rsquo;t do for a funeral home. Many also let you collect them in
            person within days instead of waiting on the mail.
          </p>
          {/* Printed as written. Whitespace preserved so a template can carry
              more than one paragraph without needing markup. */}
          {template && (
            <p className="whitespace-pre-line">{template.description}</p>
          )}
        </OrderingOption>

        <OrderingOption
          ref={afterRef}
          label="We'll order them for you"
          tagline="Best if you're not in a rush"
          selected={isAfter}
          tabbable={isAfter}
          readOnly={readOnly}
          onSelect={() => chooseOrdering(DC_ORDERING.AFTER)}
          onMoveSelection={() => moveTo(DC_ORDERING.FAMILY)}
          price={
            hasCredit ? (
              <span className="font-medium text-sage">{included} included</span>
            ) : (
              <span className="text-soil">
                {formatCurrency(firstCopyPrice)} first,{" "}
                {formatCurrency(additionalCopyPrice)} after
              </span>
            )
          }
          timing={timelineAfterOrders ?? AFTER_ORDERS_TIMELINE_FALLBACK}
        >
          <p>
            Counties don&rsquo;t give funeral homes tracking, and it can take
            longer.
          </p>
          {/* Today's stepper content, unchanged and shared with the legacy
              screen via DeathCertStepper. */}
          <DeathCertStepper
            question={question}
            answer={answer}
            onAnswer={onAnswer}
            readOnly={readOnly}
            orderedQuantity={orderedQuantity}
          />
        </OrderingOption>
      </div>
    </div>
  );
}

/**
 * A choice row that expands in place. Only the selected option's detail is
 * rendered, which is what keeps both paths on one screen without repeating the
 * timing and credit facts in each.
 *
 * A div rather than a button: the expanded body contains the quantity stepper,
 * and a button inside a button is invalid.
 */
const OrderingOption = forwardRef<
  HTMLDivElement,
  {
    label: string;
    tagline?: string;
    selected: boolean;
    /** Whether Tab stops here: the selected option, or the first when none is. */
    tabbable: boolean;
    onSelect: () => void;
    /** Hands an arrow keypress back to the group, which owns the order. */
    onMoveSelection: () => void;
    price: ReactNode;
    timing?: string | null;
    readOnly?: boolean;
    children: ReactNode;
  }
>(function OrderingOption(
  {
    label,
    tagline,
    selected,
    tabbable,
    onSelect,
    onMoveSelection,
    price,
    timing,
    readOnly = false,
    children,
  },
  ref,
) {
  const interactionClasses = readOnly
    ? selected
      ? "border-evergreen bg-ivory-light"
      : "border-olive bg-white opacity-60"
    : selected
      ? "cursor-pointer border-evergreen bg-ivory-light"
      : "cursor-pointer border-olive bg-white hover:bg-ivory-light";

  return (
    <div
      ref={ref}
      role="radio"
      aria-checked={selected}
      aria-disabled={readOnly || undefined}
      // One tab stop for the whole group: Tab reaches the selected option, and
      // the arrow keys move within it.
      tabIndex={readOnly || !tabbable ? -1 : 0}
      onClick={readOnly ? undefined : onSelect}
      onKeyDown={(e) => {
        if (readOnly) return;
        // Only when the row itself has focus. Keydowns from the quantity
        // stepper inside bubble up here, and preventDefault would swallow
        // their own Space/Enter activation.
        if (e.target !== e.currentTarget) return;
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onSelect();
          return;
        }
        if (
          e.key === "ArrowUp" ||
          e.key === "ArrowLeft" ||
          e.key === "ArrowDown" ||
          e.key === "ArrowRight"
        ) {
          e.preventDefault();
          onMoveSelection();
        }
      }}
      className={`w-full rounded-md border p-4 text-left transition ${interactionClasses}`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`mx-1 mt-1.5 h-3 w-3 shrink-0 rounded-full outline outline-2 outline-offset-2 outline-evergreen ${
            selected ? "bg-cantelope" : ""
          }`}
        />
        {/* Timing sits under the label rather than beside the price: it needs
            enough room to say what it means, and both rows carrying one is
            what lets the family compare speed as well as cost without
            expanding either. */}
        <span className="flex-1">
          <span
            className={`block text-evergreen ${selected ? "font-bold" : ""}`}
          >
            {label}
          </span>
          {tagline && (
            <span className="mt-0.5 block text-sm text-soil">{tagline}</span>
          )}
          {timing && (
            <span className="mt-0.5 block text-sm text-soil/75">{timing}</span>
          )}
        </span>
        <span className="shrink-0 text-right">{price}</span>
      </div>

      {selected && (
        <div
          className="ml-8 mt-3.5 space-y-3 border-t border-olive/45 pt-3.5 text-base leading-relaxed text-soil"
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </div>
      )}
    </div>
  );
});
